import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from '../../../utils/classNames';
import { COVER_LABELS, MAP_OBJECTS, OPEN_TENTS, STICKERS } from '../../../shared/mapLayout.mjs';

const lower = (word) => word.toLowerCase();
const entryFor = (id) => MAP_OBJECTS[id] || OPEN_TENTS[id] || STICKERS[id];
const plain = (id) => ({ key: id, label: entryFor(id).label, ids: () => id });

// The stamp list by tool and tab: Cover for things that shape a fight, Scenery for the rest. A stamp with `rows` is one choice
// here that stands for several stamps: each row picks one way they differ, and `ids` turns the options picked into the stamp.
const TABS = Object.freeze([
  { label: 'Terrain', stamps: ['tree', 'brush', 'foliage', 'boulder', 'rubble', 'fog'].map(plain) },
  { label: 'Furniture', stamps: [plain('crates'), {
    key: 'table', label: 'Table', rows: [{ label: 'Table kind', options: ['Empty', 'Filled'] }], ids: ([kind]) => `table-${lower(kind)}`,
  }, plain('bed')] },
  { label: 'Containers', stamps: ['chest', 'safe', 'wall-safe', 'strong-box'].map(plain) },
  { label: 'Light', stamps: ['campfire', 'torch', 'wall-light'].map(plain) },
  { label: 'Camp', stamps: [{
    key: 'tent',
    label: 'Tent',
    rows: [{ label: 'Tent style', options: ['Closed', 'Open'] }, { label: 'Tent color', options: ['Brown', 'Canvas', 'Red'] }],
    ids: ([style, color]) => `tent-${lower(color)}${style === 'Open' ? '-open' : ''}`,
  }] },
  { label: 'Structure', stamps: [plain('stairs'), plain('solid'), {
    key: 'lever', label: 'Lever', rows: [{ label: 'Lever kind', options: ['Wall', 'Floor'] }], ids: ([kind]) => `lever-${lower(kind)}`,
  }] },
  { label: 'Blood', stamps: ['blood-puddle', 'blood-footprints'].map(plain) },
  { label: 'Magic', stamps: [{
    key: 'magic-circle', label: 'Magic circle', rows: [{ label: 'Circle color', options: ['Blue', 'Green', 'Purple', 'Red'] }], ids: ([color]) => `magic-circle-${lower(color)}`,
  }] },
  { label: 'Bodies', stamps: [plain('body')] },
  { label: 'Vehicles', stamps: ['car', 'cart', 'truck', 'motorcycle'].map(plain) },
]);
const tabsNamed = (...labels) => Object.freeze(labels.map((label) => TABS.find((tab) => tab.label === label)));
export const STAMP_TOOLS = Object.freeze({
  cover: tabsNamed('Terrain', 'Furniture', 'Containers', 'Structure'),
  scenery: tabsNamed('Camp', 'Blood', 'Magic', 'Light', 'Bodies', 'Vehicles'),
});

const SHORT_COVER = Object.freeze({ half: 'Half', 'three-quarters': '3/4', total: 'Total' });
const coverLabel = (object) => (object.cover ? COVER_LABELS[object.cover] : object.difficult ? 'Difficult terrain'
  : object.obscures === 'heavy' ? 'Heavily Obscured' : 'No cover');
const shortCover = (object) => SHORT_COVER[object.cover] || (object.difficult && 'Difficult') || (object.obscures && 'Obscured') || '';

const firstOptions = (stamp) => (stamp.rows || []).map(({ options }) => options[0]);
// Every set of options a stamp can be chosen with.
const optionSets = (rows) => (rows || []).reduce((sets, { options }) => sets.flatMap((set) => options.map((option) => [...set, option])), [[]]);
// The tab, stamp and options that make a stamp id, or the first tab if it's on none, as a retired stamp is.
const findStamp = (id, tabs) => {
  for (const tab of tabs) {
    for (const stamp of tab.stamps) {
      const picked = optionSets(stamp.rows).find((set) => stamp.ids(set) === id);
      if (picked) return { tab, stamp, picked };
    }
  }
  return { tab: tabs[0], stamp: tabs[0].stamps[0], picked: firstOptions(tabs[0].stamps[0]) };
};

function SwatchRow({ label, modifier, options, value, onPick }) {
  return (
    <div className={classNames('map-maker__swatches', modifier)} role="radiogroup" aria-label={label}>
      {options.map((option) => (
        <button key={option} type="button" role="radio" aria-checked={value === option}
          className={classNames('map-maker__swatch', value === option && 'map-maker__swatch--active')} onClick={() => onPick(option)}>
          {option}
        </button>
      ))}
    </div>
  );
}

SwatchRow.propTypes = {
  label: PropTypes.string.isRequired,
  modifier: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  value: PropTypes.string,
  onPick: PropTypes.func.isRequired,
};

/** The stamps one map maker tool can place, by tab, with a row for each option of the chosen stamp. */
export default function MapStampPicker({ tool, value, onChange }) {
  const tabs = STAMP_TOOLS[tool];
  const current = findStamp(value, tabs);
  const [tabLabel, setTabLabel] = useState(current.tab.label);
  // The options last picked for each stamp, and the stamp last used on each tab.
  const [picks, setPicks] = useState({ [current.stamp.key]: current.picked });
  const [lastUsed, setLastUsed] = useState({ [current.tab.label]: current.stamp.key });
  const tab = tabs.find(({ label }) => label === tabLabel);
  const optionsOf = (stamp) => picks[stamp.key] || firstOptions(stamp);
  const choose = (stamp, options = optionsOf(stamp)) => {
    setPicks((all) => ({ ...all, [stamp.key]: options }));
    setLastUsed((all) => ({ ...all, [tabLabel]: stamp.key }));
    onChange(stamp.ids(options));
  };
  const openTab = (label) => {
    const next = tabs.find((entry) => entry.label === label);
    const stamp = next.stamps.find(({ key }) => key === lastUsed[label]) || next.stamps[0];
    setTabLabel(label);
    onChange(stamp.ids(optionsOf(stamp)));
  };
  const chosen = tab.stamps.find(({ key }) => key === current.stamp.key);
  return (
    <>
      <SwatchRow label="Stamp group" modifier="map-maker__swatches--tabs" options={tabs.map(({ label }) => label)} value={tab.label} onPick={openTab} />
      <div className="map-maker__swatches" role="radiogroup" aria-label="Cover stamp">
        {tab.stamps.map((stamp) => {
          const entry = entryFor(stamp.ids(optionsOf(stamp)));
          const short = shortCover(entry);
          return (
            <button key={stamp.key} type="button" role="radio" aria-checked={chosen === stamp} title={coverLabel(entry)}
              className={classNames('map-maker__swatch', chosen === stamp && 'map-maker__swatch--active')} onClick={() => choose(stamp)}>
              <span className={`map-maker__stamp map-maker__stamp--${entry.cover || (entry.difficult ? 'difficult' : 'none')}`}
                style={entry.sketchColor ? { background: entry.sketchColor, color: entry.obscures ? '#fff' : undefined } : undefined}>{entry.symbol}</span>
              {stamp.label}{short && <small>{short}</small>}
            </button>
          );
        })}
      </div>
      {(chosen?.rows || []).map((row, index) => (
        <SwatchRow key={row.label} label={row.label} options={row.options} value={optionsOf(chosen)[index]}
          onPick={(option) => choose(chosen, optionsOf(chosen).map((picked, at) => (at === index ? option : picked)))} />
      ))}
    </>
  );
}

MapStampPicker.propTypes = {
  tool: PropTypes.oneOf(Object.keys(STAMP_TOOLS)),
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

MapStampPicker.defaultProps = {
  tool: 'cover',
};
